import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';

/**
 * Flipbook - Flip-book cards with decreasing top height
 *
 * Layout: 2 portrait pages, 3 columns on first page + 2 on second.
 * Each column is split into top/bottom halves with decreasing height.
 *
 * Migrated from C# EduFrame.Img10.Generators.Flipbook
 */
export class Flipbook implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 4;
  }
  countMax(): number {
    return 12;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const count = opts.list.length;
    const cols = Math.ceil(count / 2);
    const colsPerPage = 3;
    const pagesNeeded = Math.ceil(cols / colsPerPage);
    const width = (PdfSize.A4.width - 2 * margin) / colsPerPage;
    const originY = (PdfSize.A4.height - PdfUnit.FromMillimeters(270)) / 2;

    const AVAILABLE = 270;
    const MAX_TOP = 180;
    const MIN_TOP = 90;
    const delta = (MAX_TOP - MIN_TOP) / (count - 1);

    for (let i = 0; i < pagesNeeded; i++) {
      pdf.addPage();
    }

    const cellSize = new PdfSize(width - margin * 2, PdfUnit.FromMillimeters(30));
    const leftTexts = opts.list.map((t) => t.left.getText()).filter((t) => t);
    const rightTexts = opts.list.map((t) => t.right.getText()).filter((t) => t);
    const leftFont =
      leftTexts.length > 0
        ? pdf.getPage(0).adjustFontMulti(
            leftTexts.map((t) => [t]),
            GUtil.baseFont,
            cellSize
          )
        : GUtil.baseFont;
    const rightFont =
      rightTexts.length > 0
        ? pdf.getPage(0).adjustFontMulti(
            rightTexts.map((t) => [t]),
            GUtil.baseFont,
            cellSize
          )
        : GUtil.baseFont;

    const drawFlip = (pageIdx: number, rect: PdfRect, index: number, isBottom: boolean) => {
      if (index >= count) return;
      const page = pdf.getPage(pageIdx);
      const bottomRect = new PdfRect(
        rect.left,
        rect.bottom - PdfUnit.FromMillimeters(15),
        rect.width,
        PdfUnit.FromMillimeters(15)
      );
      const imageRect = new PdfRect(
        rect.left,
        rect.top + PdfUnit.FromMillimeters(10),
        rect.width,
        rect.height - PdfUnit.FromMillimeters(25)
      );

      if (isBottom && index >= count) {
        return;
      }

      page.drawLabel(opts.list[index].left, { rect: imageRect, font: leftFont });
      page.drawLabel(opts.list[index].right, { rect: bottomRect, font: rightFont });

      page.drawRectangle(GUtil.cutPen, null, rect);
      page.drawLine(
        GUtil.foldPen,
        new PdfPoint(imageRect.left, imageRect.top),
        new PdfPoint(imageRect.right, imageRect.top)
      );
    };

    let colIndex = 0;
    for (let pageIdx = 0; pageIdx < pagesNeeded; pageIdx++) {
      for (let x = 0; x < colsPerPage && colIndex < cols; x++, colIndex++) {
        const r1Height = MAX_TOP - colIndex * delta;
        const r2Height = AVAILABLE - r1Height;

        const r1 = new PdfRect(
          margin + x * width,
          originY,
          width,
          PdfUnit.FromMillimeters(r1Height)
        );
        const r2 = new PdfRect(r1.left, r1.bottom, width, PdfUnit.FromMillimeters(r2Height));

        const topIndex = colIndex;
        const bottomIndex = colIndex + cols;

        drawFlip(pageIdx, r1, topIndex, false);
        drawFlip(pageIdx, r2, bottomIndex, true);
      }
    }
  }
}
